import { defineStore } from "pinia"

export const useSmsStore = defineStore("sms", {
  state: () => ({
    // SMS Balance
    balance: {
      loading: false,
      error: null,
      data: null,
    },

    // SMS Logs
    logs: [],
    logsLoading: false,

    // Test SMS
    testSms: {
      recipient: "",
      message: "This is a test message from CargoLink.",
      senderId: "",
      loading: false,
      error: null,
      success: null,
    },

    // Campaigns
    campaigns: [],
    campaignsLoading: false,

    // Templates
    templates: [],
    templatesLoading: false,

    // Settings
    settings: {
      defaultSenderId: "N-SMS",
      apiUrl: "https://messaging-service.co.tz",
      apiToken: "",
      dailyLimit: 1000,
      dailyCount: 0,
      enableAutomation: true,
    },
    settingsLoading: false,

    // Automation Rules
    automationRules: [
      {
        id: 1,
        name: "New User Registration",
        description: "Send welcome SMS when user registers",
        icon: "ph:user-plus",
        enabled: true,
        event: "user.registered",
      },
      {
        id: 2,
        name: "Shipment Created",
        description: "Notify shipper when shipment is created",
        icon: "ph:package",
        enabled: true,
        event: "shipment.created",
      },
      {
        id: 3,
        name: "Driver Assigned",
        description: "Notify shipper when driver is assigned",
        icon: "ph:user-check",
        enabled: true,
        event: "shipment.driver_assigned",
      },
      {
        id: 4,
        name: "Delivery Completed",
        description: "Notify shipper when delivery is complete",
        icon: "ph:check-circle",
        enabled: true,
        event: "shipment.delivered",
      },
      {
        id: 5,
        name: "Payment Received",
        description: "Confirm payment receipt to user",
        icon: "ph:currency-dollar",
        enabled: false,
        event: "payment.received",
      },
    ],

    // Analytics
    analytics: {
      monthTotal: 0,
      deliveredRate: 0,
      failedCount: 0,
      totalCost: 0,
      marketingCount: 0,
      transactionalCount: 0,
      testCount: 0,
    },
    analyticsLoading: false,

    // Connection Test
    connectionTest: {
      loading: false,
      success: false,
      error: null,
      balance: null,
    },
  }),

  getters: {
    // Today's SMS count
    todaySmsCount: (state) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      return state.logs.filter((log) => {
        if (!log.sent_at) return false
        const logDate = new Date(log.sent_at)
        logDate.setHours(0, 0, 0, 0)
        return logDate.getTime() === today.getTime()
      }).length
    },

    // Success rate
    successRate: (state) => {
      if (state.logs.length === 0) return 0
      const successful = state.logs.filter((log) => log.status === "sent" || log.status === "delivered").length
      return Math.round((successful / state.logs.length) * 100)
    },

    // Total messages sent via campaigns
    totalMessagesSent: (state) => {
      return state.campaigns.reduce((total, campaign) => {
        return total + (campaign.recipient_count || 0)
      }, 0)
    },

    // Campaign success rate
    campaignSuccessRate: (state) => {
      const total = state.campaigns.length
      if (total === 0) return 0
      const successful = state.campaigns.filter((c) => c.status === "sent").length
      return Math.round((successful / total) * 100)
    },

    // Scheduled campaigns count
    scheduledCampaignsCount: (state) => {
      return state.campaigns.filter((c) => c.status === "scheduled").length
    },

    // Draft campaigns count
    draftCampaignsCount: (state) => {
      return state.campaigns.filter((c) => c.status === "draft").length
    },

    // Active automation rules count
    activeAutomationRulesCount: (state) => {
      return state.automationRules.filter((r) => r.enabled).length
    },
  },

  actions: {
    // ===== Balance Actions =====

    async fetchBalance() {
      this.balance.loading = true
      this.balance.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/notifications/sms/balance")
        this.balance.data = response.data.data
      } catch (error) {
        console.error("Failed to fetch SMS balance:", error)
        this.balance.error = error.response?.data?.message || "Failed to fetch balance"
      } finally {
        this.balance.loading = false
      }
    },

    // ===== SMS Sending Actions =====

    async sendTestSms(recipient, message, senderId = null) {
      this.testSms.loading = true
      this.testSms.error = null
      this.testSms.success = null

      try {
        // Using this.$axios injected by pinia plugin
        const payload = {
          recipient,
          message,
        }

        if (senderId) {
          payload.sender_id = senderId
        }

        await this.$axios.post("/api/v1/notifications/sms/send", payload)

        this.testSms.success = "Test SMS sent successfully!"
        this.testSms.recipient = ""
        this.testSms.message = "This is a test message from CargoLink."

        // Refresh balance and logs
        await Promise.all([this.fetchBalance(), this.fetchLogs()])

        // Clear success message after 5 seconds
        setTimeout(() => {
          this.testSms.success = null
        }, 5000)

        return true
      } catch (error) {
        console.error("Failed to send test SMS:", error)
        this.testSms.error = error.response?.data?.message || error.response?.data?.error || "Failed to send SMS"
        return false
      } finally {
        this.testSms.loading = false
      }
    },

    // ===== Logs Actions =====

    async fetchLogs(limit = 20) {
      this.logsLoading = true

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/notifications/sms/logs", {
          params: { limit },
        })
        this.logs = response.data || []
      } catch (error) {
        console.error("Failed to fetch SMS logs:", error)
        this.logs = []
      } finally {
        this.logsLoading = false
      }
    },

    // ===== Campaign Actions =====

    async fetchCampaigns() {
      this.campaignsLoading = true

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/notifications/sms/campaigns")
        this.campaigns = response.data || []
      } catch (error) {
        console.error("Failed to fetch campaigns:", error)
        this.campaigns = []
      } finally {
        this.campaignsLoading = false
      }
    },

    async createCampaign(campaignData) {
      try {
        // Using this.$axios injected by pinia plugin
        const payload = {
          name: campaignData.name,
          message: campaignData.message,
          target_group: campaignData.targetGroup,
          custom_recipients: campaignData.targetGroup === "custom" ? campaignData.customRecipients.split(",").map((r) => r.trim()) : [],
          sender_id: campaignData.senderId || null,
          scheduled_at: campaignData.scheduleType === "scheduled" ? campaignData.scheduledDate : null,
        }

        const response = await this.$axios.post("/api/v1/notifications/sms/campaigns", payload)

        // If sending now, trigger send
        if (campaignData.scheduleType === "now" && response.data?.id) {
          await this.sendCampaign(response.data.id)
        }

        // Refresh campaigns
        await this.fetchCampaigns()

        return { success: true, data: response.data }
      } catch (error) {
        console.error("Failed to create campaign:", error)
        throw error
      }
    },

    async sendCampaign(campaignId) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.post(`/api/v1/notifications/sms/campaigns/${campaignId}/send`)

        // Refresh campaigns and logs
        await Promise.all([this.fetchCampaigns(), this.fetchLogs()])

        return { success: true }
      } catch (error) {
        console.error("Failed to send campaign:", error)
        throw error
      }
    },

    async updateCampaign(campaignId, data) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.put(`/api/v1/notifications/sms/campaigns/${campaignId}`, data)

        // Refresh campaigns
        await this.fetchCampaigns()

        return { success: true }
      } catch (error) {
        console.error("Failed to update campaign:", error)
        throw error
      }
    },

    async deleteCampaign(campaignId) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.delete(`/api/v1/notifications/sms/campaigns/${campaignId}`)

        // Refresh campaigns
        await this.fetchCampaigns()

        return { success: true }
      } catch (error) {
        console.error("Failed to delete campaign:", error)
        throw error
      }
    },

    // ===== Template Actions =====

    async fetchTemplates() {
      this.templatesLoading = true

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/notifications/sms/templates")
        this.templates = response.data || []
      } catch (error) {
        console.error("Failed to fetch templates:", error)
        this.templates = []
      } finally {
        this.templatesLoading = false
      }
    },

    async createTemplate(templateData) {
      try {
        // Using this.$axios injected by pinia plugin
        const payload = {
          name: templateData.name,
          category: templateData.category,
          icon: templateData.icon,
          message: templateData.message,
        }

        await this.$axios.post("/api/v1/notifications/sms/templates", payload)

        // Refresh templates
        await this.fetchTemplates()

        return { success: true }
      } catch (error) {
        console.error("Failed to create template:", error)
        throw error
      }
    },

    async updateTemplate(templateId, data) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.put(`/api/v1/notifications/sms/templates/${templateId}`, data)

        // Refresh templates
        await this.fetchTemplates()

        return { success: true }
      } catch (error) {
        console.error("Failed to update template:", error)
        throw error
      }
    },

    async deleteTemplate(templateId) {
      try {
        // Using this.$axios injected by pinia plugin
        await this.$axios.delete(`/api/v1/notifications/sms/templates/${templateId}`)

        // Refresh templates
        await this.fetchTemplates()

        return { success: true }
      } catch (error) {
        console.error("Failed to delete template:", error)
        throw error
      }
    },

    // ===== Settings Actions =====

    async fetchSettings() {
      this.settingsLoading = true

      try {
        // Using this.$axios injected by pinia plugin
        const response = await this.$axios.get("/api/v1/notifications/sms/settings")
        if (response.data) {
          this.settings = {
            defaultSenderId: response.data.default_sender_id || "N-SMS",
            apiUrl: response.data.api_url || "https://messaging-service.co.tz",
            apiToken: response.data.api_token || "",
            dailyLimit: response.data.daily_limit || 1000,
            dailyCount: response.data.daily_count || 0,
            enableAutomation: response.data.enable_automation !== undefined ? response.data.enable_automation : true,
          }

          // Load automation rules if available
          if (response.data.automation_rules) {
            this.automationRules = response.data.automation_rules
          }
        }
      } catch (error) {
        console.error("Failed to fetch SMS settings:", error)
      } finally {
        this.settingsLoading = false
      }
    },

    async updateSettings(settingsData) {
      this.settingsLoading = true

      try {
        // Using this.$axios injected by pinia plugin
        const payload = {
          default_sender_id: settingsData.defaultSenderId,
          api_url: settingsData.apiUrl,
          api_token: settingsData.apiToken,
          daily_limit: settingsData.dailyLimit,
          enable_automation: settingsData.enableAutomation,
          automation_rules: this.automationRules,
        }

        await this.$axios.put("/api/v1/notifications/sms/settings", payload)

        // Update local state
        this.settings = { ...settingsData }

        // Refresh balance to verify new settings work
        await this.fetchBalance()

        return { success: true }
      } catch (error) {
        console.error("Failed to save settings:", error)
        throw error
      } finally {
        this.settingsLoading = false
      }
    },

    async testApiConnection() {
      this.connectionTest.loading = true
      this.connectionTest.success = false
      this.connectionTest.error = null
      this.connectionTest.balance = null

      try {
        // Using this.$axios injected by pinia plugin
        // First save the current settings temporarily to test them
        const tempPayload = {
          default_sender_id: this.settings.defaultSenderId,
          api_url: this.settings.apiUrl,
          api_token: this.settings.apiToken,
        }

        // Update settings
        await this.$axios.put("/api/v1/notifications/sms/settings", tempPayload)

        // Test connection by checking balance
        const balanceResponse = await this.$axios.get("/api/v1/notifications/sms/balance")

        if (balanceResponse.data?.success) {
          this.connectionTest.success = true
          this.connectionTest.balance = balanceResponse.data.data?.balance || "N/A"
          return { success: true, balance: this.connectionTest.balance }
        } else {
          throw new Error("Failed to retrieve balance")
        }
      } catch (error) {
        console.error("API connection test failed:", error)
        this.connectionTest.error = error.response?.data?.error || error.response?.data?.message || "Connection failed. Please check your credentials."
        throw error
      } finally {
        this.connectionTest.loading = false
      }
    },

    updateAutomationRule(ruleId, enabled) {
      const rule = this.automationRules.find((r) => r.id === ruleId)
      if (rule) {
        rule.enabled = enabled
      }
    },

    // ===== Analytics Actions =====

    async fetchAnalytics(startDate = null, endDate = null) {
      this.analyticsLoading = true

      try {
        // Using this.$axios injected by pinia plugin
        const params = {}
        if (startDate) params.start_date = startDate
        if (endDate) params.end_date = endDate

        const response = await this.$axios.get("/api/v1/notifications/sms/analytics", { params })

        if (response.data) {
          this.analytics = response.data
        }
      } catch (error) {
        console.error("Failed to fetch analytics:", error)
        // Set to zero values on error
        this.analytics = {
          monthTotal: 0,
          deliveredRate: 0,
          failedCount: 0,
          totalCost: 0,
          marketingCount: 0,
          transactionalCount: 0,
          testCount: 0,
        }
      } finally {
        this.analyticsLoading = false
      }
    },

    // ===== Helper Actions =====

    extractVariables(message) {
      const regex = /\{([^}]+)\}/g
      const variables = []
      let match

      while ((match = regex.exec(message)) !== null) {
        variables.push(match[1])
      }

      return variables
    },

    // Reset test SMS form
    resetTestForm() {
      this.testSms = {
        recipient: "",
        message: "This is a test message from CargoLink.",
        senderId: "",
        loading: false,
        error: null,
        success: null,
      }
    },

    // Clear connection test results
    clearConnectionTest() {
      this.connectionTest = {
        loading: false,
        success: false,
        error: null,
        balance: null,
      }
    },

    // Initialize store (fetch all data)
    async initialize() {
      await Promise.all([this.fetchBalance(), this.fetchLogs(), this.fetchSettings(), this.fetchCampaigns(), this.fetchTemplates(), this.fetchAnalytics()])
    },
  },
})
